import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, wordsRefused, type FriendIntroPage as IntroData } from '../../api';
import { useChatsLive, useChatsSnapshot } from '../../chats-store';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { goBack, goFriends, navigate } from '../../router';
import { BACK_CHEVRON, Face, FLAME, FriendsBar, Sparkle, TILTS, zonePlace } from './friend-parts';
import { useFriendsConfig } from '../../client-config';

type View = 'intro' | 'opener' | 'waiting' | 'both_in' | 'lapsed' | 'closed';
type Asked = { q: string; a?: string; soft?: boolean };
type IntroCard = { cls?: string; body: ReactElement };

/** Where the two stand, as this page shows it. */
function viewOf(p: IntroData): View {
  if (p.status === 'open') return 'both_in';
  if (p.status === 'lapsed' || p.me.answer === 'no') return 'lapsed';
  if (p.status === 'closed') return 'closed';
  if (p.me.answer === 'yes') return 'waiting';
  return 'intro';
}

const chatOf = (roomId: string): string => `/talk?mode=chat&room=${encodeURIComponent(roomId)}`;
const NOTE_TINTS = ['#FFF1C7', '#DDEBDC', '#F7DCD0'];
const NOTE_TILTS = ['-3deg', '2deg', '1.5deg', '-1.5deg'];
const STICKY_TINTS = ['#FFF4C9', '#DDEBDC', '#F7DCD0'];
const CLOSE = <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /></svg>;
const SEND = <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><path d="M12 19V6M6 11l6-6 6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const TICK = <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;

/**
 * An introduction — `/friends/intro/:roomId` (2026-10-26 founder; mocks Intro, FirstLine, Waiting, BothIn; the intro
 * itself the original look made smaller to fit one screen since 2026-10-27, mock "E smaller"). The picture of the two of
 * them, the other's line and clock; cards that turn on their own — why the two of them (the hunch and the venn), Ask
 * me anything (a conversation in its card, answered from the two cards and the introduction alone),
 * easy things to chat about; then yes — with an opener they pick or write, or none (Ember starts the chat only when neither picked one) — or not now. After a
 * yes: waiting for the other — the same page, their card and the cards still there to read and ask about, the foot saying
 * they said yes (2026-11-02 jam, mock A: the separate waiting page hid the card Ember sends them to) — or both in and
 * the chat opens. An introduction that lapsed (a no, or three days) says only that it didn't work out this time.
 */
export function FriendIntroPage({ roomId }: { roomId: string }): ReactElement {
  // Meet people's served limits and sentences (2026-11-01 Riley, client-config.ts): redrawn when the config changes
  const served = useFriendsConfig();
  const [page, setPage] = useState<IntroData | null>(null);
  const [failed, setFailed] = useState<false | 'not_found' | 'load_failed'>(false);
  const [view, setView] = useState<View>('intro');
  const viewRef = useRef<View>('intro');
  viewRef.current = view;
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  // The questions asked and their answers (none while one is being answered), oldest first.
  const [asked, setAsked] = useState<Asked[]>([]);
  const stream = useRef<HTMLDivElement>(null);
  const [question, setQuestion] = useState('');
  const [pick, setPick] = useState<number | 'own'>(0);
  const [own, setOwn] = useState('');

  const load = (): void => {
    emberApi.friendIntro(roomId)
      .then((p) => {
        setPage(p);
        // Picking an opener is theirs to finish — unless the introduction is over meanwhile; anything else follows where the
        // two stand now.
        const next = viewOf(p);
        if (viewRef.current !== 'opener' || next === 'lapsed' || next === 'closed') { setView(next); viewRef.current = next; }
        if (!p.openers.length) setPick('own');
      })
      .catch((err: unknown) => {
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(`/friends/intro/${roomId}`)}`, { replace: true }); return; }
        const gone = err instanceof ApiError && err.status === 404;
        if (!gone) trackFailed('friend_intro_load', apiFailureReason(err));
        setFailed(gone ? 'not_found' : 'load_failed');
      });
  };
  useEffect(() => {
    load();
    // The other's answer lands while they are away: the page reads again when they come back to it.
    const again = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [roomId]);
  // While it is on screen, a change to their Chats (the live channel: the chat opening, the introduction lapsing) reads the
  // page again, so the other's answer shows here without leaving it — while waiting on the other's yes (2026-10-26
  // walk-through: "Waiting for Hana" stood after her yes) and while still deciding (2026-10-27: the other's not now stood
  // unseen until a yes sent into it came back refused).
  useChatsLive(true);
  const chats = useChatsSnapshot().data;
  useEffect(() => { if (chats) load(); }, [chats]);
  // The newest question goes to the top of the conversation with its answer under it; the earlier ones sit above.
  useEffect(() => {
    const box = stream.current;
    const pairs = box?.querySelectorAll<HTMLElement>('.nf-qa-pair');
    const last = pairs?.[pairs.length - 1];
    if (box && last) box.scrollTo({ top: last.offsetTop, behavior: 'smooth' });
  }, [asked]);

  const show = (next: View): void => {
    setView(next);
    window.scrollTo({ top: 0 });
  };

  const answer = async (accept: boolean, opener = ''): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await emberApi.answerFriendIntro(roomId, accept, opener);
      // Not now: back to the /friends behind, not a second one over it (release check 2026-10-27).
      if (!accept) { goFriends(); return; }
      setPage((p) => (p ? { ...p, status: r.room_status ?? p.status, me: { ...p.me, answer: 'yes', opener: opener || null } } : p));
      show(r.room_status === 'open' ? 'both_in' : 'waiting');
      // Their yes opened the chat: the page read again brings the other's first line ("X already said …"), which is said
      // only once the chat is open (2026-10-27 walk-through: the second to say yes saw it only after a reload).
      if (r.room_status === 'open') load();
    } catch (err: unknown) {
      const code = apiErrorCode(err);
      trackFailed('friend_intro_answer', apiFailureReason(err));
      // Answered already, or the introduction is over: the page as it stands now.
      if (code === 'already_answered' || code === 'room_closed' || code === 'not_invited') { setSheet(false); setView('intro'); viewRef.current = 'intro'; load(); return; }
      // New friends is not open to them now: the page that says so, as everywhere else it is not.
      if (code === 'not_eligible') { navigate('/friends', { replace: true }); return; }
      // Their first line did not pass review: nothing was said yet, and the sheet stays open to pick or write another.
      const words = wordsRefused(err, { opener: 'A first line' });
      if (words) { toast.info(words.message); return; }
      toast.error('Couldn’t send that. Try again?');
    } finally {
      setBusy(false);
    }
  };

  const ask = async (text: string): Promise<void> => {
    const q = text.trim();
    if (!q || asked.some((x) => x.a === undefined)) return;
    setAsked((list) => [...list, { q }]);
    setQuestion('');
    const settle = (a: string, soft = false): void => setAsked((list) => list.map((x) => (x.a === undefined ? { q, a, soft } : x)));
    try {
      const r = await emberApi.askAboutFriend(roomId, q);
      if (r.answer) settle(r.answer);
      else settle('I can’t answer that one right now. Try again in a bit.', true);
    } catch (err: unknown) {
      trackFailed('friend_ask', apiFailureReason(err));
      settle(apiErrorCode(err) === 'too_many'
        ? 'That’s a lot of questions for one day! Ask me again tomorrow.'
        : 'I can’t answer that one right now. Try again in a bit.', true);
    }
  };

  if (failed === 'not_found') return <PageFailure title="This introduction isn’t here." detail="It may be for someone else, or the link is old." logState="not_found" />;
  if (failed && !page) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const { me, them, why, easy, openers } = page;
  const zone = zonePlace(them.zone);

  if (view === 'both_in') {
    return (
      <div className="nf-night" data-log-seen="friend_intro" data-log-state="both_in" data-log-match-id={roomId}>
        <main className="nf-page is-night">
          <FriendsBar />
          <div className="nf-center-body">
            <div className="nf-bothin">
              <svg className="nf-bothin-confetti" viewBox="0 0 330 250" aria-hidden>
                <rect x="40" y="30" width="10" height="5" rx="2" fill="#FFB010" transform="rotate(25 45 32)" />
                <rect x="270" y="44" width="10" height="5" rx="2" fill="#F4720C" transform="rotate(-30 275 46)" />
                <rect x="90" y="200" width="9" height="5" rx="2" fill="#7FB98A" transform="rotate(40 94 202)" />
                <rect x="236" y="206" width="9" height="5" rx="2" fill="#FFB010" transform="rotate(-15 240 208)" />
                <circle cx="160" cy="20" r="3" fill="#F7DCD0" /><circle cx="300" cy="140" r="3" fill="#FFB010" /><circle cx="24" cy="150" r="3" fill="#7FB98A" />
                <rect x="200" y="14" width="8" height="4" rx="2" fill="#B9A6E6" transform="rotate(20 204 16)" />
                <rect x="18" y="80" width="8" height="4" rx="2" fill="#B9A6E6" transform="rotate(-25 22 82)" />
              </svg>
              <Face avatar={me.avatar} size={122} className="is-ring-night is-left nf-bob" />
              <Face avatar={them.avatar} size={122} className="is-ring-night is-right nf-bob is-late" />
              <span className="nf-bothin-flame">{FLAME}</span>
              <Sparkle size={14} color="#FFE7B0" className="nf-twinkle nf-bothin-spark" />
            </div>
            <div className="nf-center-text">
              <span className="nf-kicker is-gold">hooray!</span>
              <h1 className="nf-title is-center">You’re both in</h1>
            </div>
            {them.opener ? (
              <section className="nf-bothin-said">
                <Face avatar={them.avatar} size={40} />
                <div>
                  <span>{them.name} already said</span>
                  <p>“{them.opener}”</p>
                </div>
              </section>
            ) : null}
          </div>
          <div className="nf-foot is-night">
            <button type="button" className="nf-cta is-block is-gold" data-log="friend_say_hi" data-log-match-id={roomId} onClick={() => navigate(chatOf(roomId))}>{them.opener ? 'Say hi back' : 'Say hi'}</button>
          </div>
        </main>
      </div>
    );
  }

  if (view === 'lapsed' || view === 'closed') {
    return (
      <main className="nf-page" data-log-seen="friend_intro" data-log-state={view} data-log-match-id={roomId}>
        <FriendsBar />
        <div className="nf-center-body">
          <div className="nf-pair is-apart">
            <Face avatar={me.avatar} size={96} className="is-ring-white is-left" />
            <Face avatar={them.avatar} size={96} className="is-ring-white is-right is-faded" />
          </div>
          <div className="nf-center-text">
            {/* The one who said not now hears their own answer, not "didn't work out" (2026-10-27 walk-through). */}
            <h1 className="nf-title is-step">{view === 'closed' ? 'This chat has wrapped up' : me.answer === 'no' ? 'You said not now' : 'Didn’t work out this time'}</h1>
            {view === 'lapsed' && me.answer === 'no' && <p>I won’t introduce you two again.{me.looking ? ' I’m still looking for you.' : ''}</p>}
            {view === 'lapsed' && me.answer !== 'no' && me.looking && <p>Ember is still looking.</p>}
          </div>
        </div>
        <div className="nf-foot">
          <Link to="/friends" className="nf-cta is-block" data-log="friend_intro_done_back" data-log-state={view}>Back to Meet people</Link>
        </div>
      </main>
    );
  }

  if (view === 'opener') {
    const chosen = pick === 'own' ? own.trim() : openers[pick] ?? '';
    return (
      <main className="nf-page" data-log-seen="friend_intro" data-log-state="opener" data-log-match-id={roomId}>
        <FriendsBar left={<button type="button" className="nf-iconbtn" aria-label="Back" data-log="friend_opener_back" onClick={() => show('intro')}>{BACK_CHEVRON}</button>} />
        <div className="nf-step">
          <div className="nf-step-head">
            <span className="nf-kicker is-go">almost there!</span>
            <h1 className="nf-title is-step">Pick an opener</h1>
            <p className="nf-step-sub">It’s the first thing {them.name} sees from you.</p>
          </div>
          <div className="nf-notes">
            {openers.map((o, i) => (
              <button
                type="button"
                key={o}
                className={`nf-note-card${pick === i ? ' is-on' : ''}`}
                style={{ background: NOTE_TINTS[i % NOTE_TINTS.length], transform: `rotate(${NOTE_TILTS[i % NOTE_TILTS.length]})` }}
                aria-pressed={pick === i}
                data-log="friend_opener_pick"
                data-log-state="suggested"
                data-log-position={i + 1}
                onClick={() => setPick(i)}
              >
                <span>{o}</span>
                {pick === i && <span className="nf-note-check">{TICK}</span>}
              </button>
            ))}
            {pick === 'own' ? (
              <label className="nf-note-card is-own is-on" style={{ transform: `rotate(${NOTE_TILTS[3]})` }}>
                <textarea autoFocus={openers.length > 0} value={own} maxLength={600} placeholder="Write my own..." aria-label="Write my own opener" onChange={(e) => setOwn(e.target.value)} />
                <span className="nf-note-check">{TICK}</span>
              </label>
            ) : (
              <button
                type="button"
                className="nf-note-card is-own"
                style={{ transform: `rotate(${NOTE_TILTS[3]})` }}
                aria-pressed={false}
                data-log="friend_opener_pick"
                data-log-state="own"
                onClick={() => setPick('own')}
              >
                <span>Write my own...</span>
              </button>
            )}
          </div>
        </div>
        <div className="nf-foot">
          <button type="button" className="nf-cta is-block" disabled={busy || !chosen} data-log="friend_opener_use" data-log-state={pick === 'own' ? 'own' : 'suggested'} data-log-match-id={roomId} onClick={() => { void answer(true, chosen); }}>Use this one</button>
          <button type="button" className="nf-text-btn" disabled={busy} data-log="friend_opener_skip" data-log-match-id={roomId} onClick={() => { void answer(true, ''); }}>Skip</button>
        </div>
      </main>
    );
  }

  const waiting = view === 'waiting';
  const pending = asked.some((x) => x.a === undefined);
  // A question leaves the chips once it is asked; one that couldn't be answered comes back.
  const unasked = page.asks.filter((q) => !asked.some((x) => x.q === q && !x.soft));
  const hasVenn = why.mine.length + why.both.length + why.theirs.length > 0;
  // The cards, those with something on them: why the two of them, Ask me anything, easy things (the this-or-that card is
  // gone with the this-or-that, 2026-10-31 founder).
  const cards: IntroCard[] = [];
  if (why.hunch || hasVenn) {
    cards.push({
      body: (
        <>
          <div className="nf-cardlet-head"><h2>Why you two</h2><span className="nf-hand">my hunch</span></div>
          {why.hunch && <p>{why.hunch}</p>}
          {hasVenn && (
            <div className="nf-venn">
              <span className="nf-venn-who is-me">You</span>
              <span className="nf-venn-who is-them">{them.name}</span>
              <div className="nf-venn-col is-mine">{why.mine.slice(0, 3).map((x) => <span key={x}>{x}</span>)}</div>
              <div className="nf-venn-both">
                {why.both.slice(0, 3).map((x, i) => <span key={x} style={{ transform: `rotate(${TILTS[(i + 2) % TILTS.length]})` }}>{x}</span>)}
              </div>
              <div className="nf-venn-col is-theirs">{why.theirs.slice(0, 3).map((x) => <span key={x}>{x}</span>)}</div>
            </div>
          )}
        </>
      ),
    });
  }
  cards.push({
    cls: 'is-ask',
    body: (
      <>
        <div className="nf-ask-head">
          <span className="nf-ask-flame">{FLAME}</span>
          <div>
            <h2>Ask me anything</h2>
            <p>About {them.name}, you two, or how this works</p>
          </div>
        </div>
        <div className="nf-ask-stream" ref={stream} aria-live="polite">
          {asked.map((x, i) => (
            <div className="nf-qa-pair" key={i}>
              <div className="nf-q">{x.q}</div>
              {x.a === undefined
                ? <div className="nf-a is-wait" aria-label="Ember is answering"><span className="nf-typing is-inline" aria-hidden><i /><i /><i /></span></div>
                : <div className={`nf-a${x.soft ? ' is-soft' : ''}`}>{x.a}</div>}
            </div>
          ))}
          {unasked.length > 0 && (
            <div className="nf-ask-chips">
              {unasked.map((q) => (
                <button
                  type="button"
                  key={q}
                  className="nf-ask-chip"
                  disabled={pending}
                  data-log="friend_ask"
                  data-log-state="chip"
                  data-log-match-id={roomId}
                  onClick={() => { void ask(q); }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
        <form className="nf-ask-form" onSubmit={(e) => { e.preventDefault(); void ask(question); }}>
          <input value={question} maxLength={300} placeholder="Type a question" aria-label="Ask Ember" onChange={(e) => setQuestion(e.target.value)} />
          <button type="submit" className="nf-ask-send" aria-label="Ask" disabled={pending || !question.trim()} data-log="friend_ask" data-log-state="typed" data-log-match-id={roomId}>{SEND}</button>
        </form>
      </>
    ),
  });
  if (easy.length > 0) {
    cards.push({
      cls: 'is-easy',
      body: (
        <>
          <h2>Easy things to chat about</h2>
          <div className="nf-easy-notes">
            {easy.map((e, i) => <span className="nf-sticky" key={e} style={{ background: STICKY_TINTS[i % STICKY_TINTS.length] }}>{e}</span>)}
          </div>
        </>
      ),
    });
  }

  return (
    <main className="nf-page is-intro" data-log-seen="friend_intro" data-log-state={waiting ? 'waiting' : 'intro'} data-log-match-id={roomId}>
      <FriendsBar left={<button type="button" className="nf-iconbtn" aria-label="Close" data-log="friend_intro_close" onClick={() => goBack(null, '/friends')}>{CLOSE}</button>}>
        <span className="nf-bar-title">An intro from Ember</span>
      </FriendsBar>

      <div className="nf-intro">
        <section className="nf-meet" aria-hidden>
          <svg className="nf-meet-art" viewBox="0 0 358 200" preserveAspectRatio="xMidYMid slice">
            <circle cx="30" cy="190" r="100" fill="#F6EAD0" />
            <circle cx="340" cy="8" r="80" fill="#E3ECE0" />
          </svg>
          <span className="nf-meet-note">{waiting ? 'fingers crossed!' : 'I have a good feeling about this!'}</span>
          <span className="nf-meet-pair">
            <span className="nf-meet-who">
              <Face avatar={me.avatar} size={56} className="is-ring-card nf-bob" />
              <span className="nf-meet-name is-me">You</span>
            </span>
            <span className="nf-meet-mid">
              <span className="nf-meet-flame">{FLAME}</span>
              <Sparkle size={11} className="nf-twinkle nf-meet-s1" />
              <Sparkle size={8} color="#F4720C" className="nf-twinkle is-late nf-meet-s2" />
            </span>
            <span className="nf-meet-who">
              <Face avatar={them.avatar} size={56} className={`is-ring-card ${waiting ? 'is-faded' : 'nf-bob is-late'}`} />
              <span className="nf-meet-name is-them">{them.name}</span>
            </span>
          </span>
        </section>

        <section className="nf-intro-who">
          <h1 className="nf-intro-title">{waiting ? `Waiting for ${them.name}` : `Say hi to ${them.name}`}</h1>
          {them.line && <p className="nf-intro-line">“{them.line}”</p>}
          {zone && <span className="nf-meta-chip">{zone}</span>}
        </section>

        <IntroCards cards={cards} />
        {/* How long it waits (2026-10-31 founder: 72 小时作废要告诉用户). */}
        <div className="nf-facts"><span>First names only</span><span>Leave anytime</span><span>I’m nearby</span>{!waiting && <span className="is-whole">{served.words.intro_open}</span>}</div>
      </div>

      {waiting ? (
        <div className="nf-foot">
          <span className="nf-said"><span className="nf-typing is-inline" aria-hidden><i /><i /><i /></span>{me.opener ? 'You said yes · opener saved' : 'You said yes'}</span>
          <button type="button" className="nf-cta is-block is-dark" data-log="friend_back_to_chat" data-log-match-id={roomId} onClick={() => navigate('/talk?mode=chat')}>Back to chat</button>
        </div>
      ) : (
        <div className="nf-foot is-row">
          <button type="button" className="nf-text-btn" data-log="friend_intro_not_now" data-log-state="ask" data-log-match-id={roomId} onClick={() => setSheet(true)}>Not now</button>
          <button type="button" className="nf-cta" data-log="friend_intro_yes" data-log-match-id={roomId} onClick={() => show('opener')}>Yes, say hi to {them.name}</button>
        </div>
      )}

      {sheet && (
        <BottomSheet
          title="Not now?"
          description={`No worries. ${them.name} will only hear it didn’t work out this time. I won’t introduce you two again, and I’ll keep looking for you.`}
          logSeen="friend_intro_not_now_sheet"
          onClose={() => setSheet(false)}
        >
          <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="friend_intro_not_now" data-log-state="confirm" data-log-match-id={roomId} onClick={() => { void answer(false); }}>Not now</button>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="friend_intro_not_now" data-log-state="keep" data-log-match-id={roomId} onClick={() => setSheet(false)}>Go back</button>
        </BottomSheet>
      )}
    </main>
  );
}

/**
 * The introduction's cards, side by side: on to the next every four seconds, round and round, until the person takes
 * hold of them — a touch, a scroll by hand, a question typed — and from then on they turn only by hand (2026-10-27
 * founder: Ask me anything is one of them, and a card turning away under its answer is no good). Nothing turns on its
 * own for someone whose system asks for less motion. The dots say which card is in front.
 */
function IntroCards({ cards }: { cards: IntroCard[] }): ReactElement {
  const row = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [at, setAt] = useState(0);
  const step = (): number => {
    const first = row.current?.firstElementChild as HTMLElement | null;
    return first ? first.offsetWidth + 12 : 0;
  };
  const hold = (): void => window.clearInterval(timer.current);
  useEffect(() => {
    if (cards.length < 2 || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    timer.current = window.setInterval(() => {
      const el = row.current;
      const s = step();
      if (!el || !s) return;
      el.scrollTo({ left: ((Math.round(el.scrollLeft / s) + 1) % cards.length) * s, behavior: 'smooth' });
    }, 4000);
    return hold;
  }, [cards.length]);
  return (
    <>
      <div className="nf-cards" ref={row} onPointerDown={hold} onWheel={hold} onFocus={hold} onScroll={(e) => { const s = step(); if (s) setAt(Math.round(e.currentTarget.scrollLeft / s)); }}>
        {cards.map((c, i) => <div className={`nf-cardlet${c.cls ? ` ${c.cls}` : ''}`} key={i}>{c.body}</div>)}
      </div>
      {cards.length > 1 && <div className="nf-dots" aria-hidden>{cards.map((_, i) => <i key={i} className={i === at ? 'is-on' : undefined} />)}</div>}
    </>
  );
}
